import { useBrokerContextSafe } from '@/app/context';
import { useMemo } from 'react';
import {
  type BrokerGuardAction,
  type BrokerWriteGuard,
  type BrokerWriteResource,
  resolveBrokerWriteGuard,
} from '../utils/broker-status.util';
import { useBrokerDetails } from './use-broker-details.hook';

/**
 * Trava criar/editar cotação ou proposta quando a corretora do contexto está
 * inativa. O `selectedBroker` é retrato do localStorage (pode estar velho), por
 * isso o status vem de uma consulta fresca pelo código.
 *
 * "Todos Corretores" (sem corretora selecionada) nunca trava: a Api barra por
 * linha com 403.
 *
 * `action='other'` cobre as demais ações (e-mail, transmitir, assinatura...)
 * com o aviso "Não é possível realizar esta ação.".
 */
export function useSelectedBrokerWriteGuard(
  resource: BrokerWriteResource,
  action: BrokerGuardAction = 'write'
): BrokerWriteGuard {
  const brokerCtx = useBrokerContextSafe();
  const code = brokerCtx?.selectedBroker?.code?.trim() ?? '';
  const hasSelectedBroker = code.length > 0;

  const { isLoading, isError, isActive } = useBrokerDetails(code, {
    enabled: hasSelectedBroker,
  });

  return useMemo(
    () =>
      resolveBrokerWriteGuard({
        hasSelectedBroker,
        details: { isLoading, isError, isActive },
        resource,
        action,
      }),
    [hasSelectedBroker, isLoading, isError, isActive, resource, action]
  );
}
